import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Package,
  DollarSign,
  Scan,
  Trash2,
  ShoppingBag,
  Calendar,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const CollectionPage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [collection, setCollection] = useState([]);
  const [stats, setStats] = useState(null);
  const [valueHistory, setValueHistory] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // Allow sellers with any form of active subscription
    const canAccessCollection = user?.subscription_active || user?.membership_status === 'gold' || 
                                user?.membership_status === 'elite' || user?.membership_status === 'Creator' ||
                                user?.is_seller;
    if (!canAccessCollection) {
      navigate('/subscription');
      return;
    }
    fetchCollection();
  }, [isAuthenticated, user]);

  const fetchCollection = async () => {
    try {
      const response = await api().get('/collection');
      setCollection(response.data.items || []);
      setStats(response.data.stats || {});
      setValueHistory(response.data.value_history || []);
    } catch (error) {
      console.error('Failed to fetch collection:', error);
      toast.error('Failed to load collection');
    } finally {
      setLoading(false);
    }
  };

  const removeFromCollection = async (itemId) => {
    try {
      await api().delete(`/collection/${itemId}`);
      toast.success('Removed from collection');
      fetchCollection();
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove item');
    }
  };

  const listForSale = (item) => {
    navigate('/sell', {
      state: {
        prefill: {
          ...item.card_data,
          price: item.estimated_value?.toFixed(2) || ''
        }
      }
    });
  };

  if (loading) return <PageLoader />;

  const totalValue = stats?.total_value || 0;
  const valueChange = stats?.value_change_30d || 0;
  const valueChangePercent = stats?.value_change_percent || 0;
  const valueDirection = valueChange > 0 ? 'up' : valueChange < 0 ? 'down' : 'neutral';

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/scanner')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Scanner
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-heading font-bold text-4xl uppercase text-white">My Collection</h1>
            </div>
            <p className="text-zinc-400">Track your cards and total portfolio value</p>
          </div>
          
          <Link to="/scanner">
            <Button className="btn-primary" data-testid="scan-card-btn">
              <Scan className="w-4 h-4 mr-2" />
              Scan New Card
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="stat-card p-6">
            <DollarSign className="w-8 h-8 text-emerald-400 mb-3" />
            <p className="text-3xl font-mono font-bold text-white">
              ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-zinc-500">Total Value</p>
          </div>
          
          <div className="stat-card p-6">
            <div className={`w-8 h-8 mb-3 ${
              valueDirection === 'up' ? 'text-emerald-400' :
              valueDirection === 'down' ? 'text-red-400' : 'text-zinc-400'
            }`}>
              {valueDirection === 'up' ? <TrendingUp className="w-8 h-8" /> :
               valueDirection === 'down' ? <TrendingDown className="w-8 h-8" /> :
               <Minus className="w-8 h-8" />}
            </div>
            <p className={`text-3xl font-mono font-bold ${
              valueDirection === 'up' ? 'text-emerald-400' :
              valueDirection === 'down' ? 'text-red-400' : 'text-white'
            }`}>
              {valueDirection === 'up' ? '+' : ''}{valueChangePercent.toFixed(1)}%
            </p>
            <p className="text-sm text-zinc-500">30-Day Change</p>
          </div>
          
          <div className="stat-card p-6">
            <Package className="w-8 h-8 text-blue-400 mb-3" />
            <p className="text-3xl font-mono font-bold text-white">{collection.length}</p>
            <p className="text-sm text-zinc-500">Total Cards</p>
          </div>
          
          <div className="stat-card p-6">
            <ShoppingBag className="w-8 h-8 text-amber-400 mb-3" />
            <p className="text-3xl font-mono font-bold text-white">
              {collection.filter(c => c.listed_for_sale).length}
            </p>
            <p className="text-sm text-zinc-500">Listed for Sale</p>
          </div>
        </div>

        {/* Value History Chart */}
        {valueHistory.length > 0 && (
          <div className="stat-card p-6 mb-8">
            <h2 className="font-heading font-bold text-xl text-white mb-4">Portfolio Value History</h2>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={valueHistory}>
                  <defs>
                    <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#71717A" 
                    fontSize={11}
                    tickFormatter={(val) => val.slice(5)}
                  />
                  <YAxis 
                    stroke="#71717A" 
                    fontSize={11}
                    tickFormatter={(val) => `$${val.toLocaleString()}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181B', 
                      border: '1px solid #27272A',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#A1A1AA' }}
                    formatter={(value) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 'Value']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    fill="url(#valueGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Collection Items */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-heading font-bold text-xl text-white">Your Cards</h2>
        </div>

        {collection.length === 0 ? (
          <div className="stat-card p-12 text-center">
            <Wallet className="w-16 h-16 text-zinc-600 mx-auto mb-6" />
            <h3 className="font-heading font-bold text-2xl text-white mb-2">No Cards Yet</h3>
            <p className="text-zinc-400 mb-6">
              Start scanning cards to build your collection and track their value over time.
            </p>
            <Link to="/scanner">
              <Button className="btn-primary" data-testid="empty-scan-btn">
                <Scan className="w-4 h-4 mr-2" />
                Scan Your First Card
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {collection.map((item) => (
              <div 
                key={item.id} 
                className="stat-card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                data-testid={`collection-item-${item.id}`}
              >
                {/* Card Image Placeholder */}
                <div className="w-16 h-22 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="font-heading font-bold text-2xl text-zinc-600">
                    {item.card_data?.player_name?.charAt(0) || '?'}
                  </span>
                </div>
                
                {/* Card Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white truncate">
                    {item.card_data?.player_name}
                  </h3>
                  <p className="text-sm text-zinc-400 truncate">
                    {item.card_data?.year} {item.card_data?.set_name}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-zinc-500">
                      {item.card_data?.grade_company} {item.card_data?.grade_value || ''}
                    </span>
                    {item.card_data?.parallel && (
                      <span className="text-xs text-amber-400">{item.card_data.parallel}</span>
                    )}
                    {item.listed_for_sale && (
                      <span className="badge badge-emerald text-xs">Listed</span>
                    )}
                  </div>
                </div>
                
                {/* Value */}
                <div className="text-right">
                  <p className="font-mono text-xl font-bold text-emerald-400">
                    ${item.estimated_value?.toFixed(2) || '0.00'}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <Calendar className="w-3 h-3" />
                    {new Date(item.created_at).toLocaleDateString()}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  {!item.listed_for_sale && (
                    <Button
                      size="sm"
                      onClick={() => listForSale(item)}
                      className="btn-primary h-9"
                      data-testid={`list-${item.id}`}
                    >
                      <ShoppingBag className="w-4 h-4 mr-1" />
                      List
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeFromCollection(item.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9"
                    data-testid={`remove-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CollectionPage;
